import React, { useEffect, useRef, useState } from "react";
import { OlaMaps } from "olamaps-web-sdk";
import axios from "axios";
import { BASE_URL, olaMapsApiKey } from "../../config";

interface PopulatedUser {
    _id: string;
    name?: string;
    email?: string;
    phoneNumber?: number;
    phoneCode?: string;
    profilePicture?: string;
}

interface PinLocation {
    _id: string;
    role: "parent" | "teacher";
    location: {
        type: "Point";
        coordinates: [number, number]; // [lng, lat]
    };
    userId: PopulatedUser;
}

interface AutocompletePrediction {
    description: string;
    geometry?: {
        location: {
            lat: number;
            lng: number;
        };
    };
}

const calculateZoom = (radiusInMeters: number): number => {
    const radiusInKm = radiusInMeters / 1000;

    // Higher radius => smaller zoom value (zoomed out)
    // Example mapping:
    // 1 km  -> ~15
    // 5 km  -> ~13
    // 10 km -> ~12
    // 20 km -> ~11
    const zoom = 15 - radiusInKm * 0.02;

    // Clamp to valid Ola Maps range 0–15
    return Math.max(0, Math.min(15, Math.round(zoom)));
};

const MapView: React.FC = () => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any | null>(null);
    const markersRef = useRef<any[]>([]);

    const [center, setCenter] = useState<{ lat: number; lng: number }>({
        lat: 12.9716,
        lng: 77.5946,
    });
    const [radius, setRadius] = useState<number>(5000);
    const [pins, setPins] = useState<PinLocation[]>([]);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isLoadingPins, setIsLoadingPins] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const autocompleteTimerRef = useRef<number | undefined>(undefined);
    const [hasInitializedCenterFromGeo, setHasInitializedCenterFromGeo] =
        useState(false);

    useEffect(() => {
        if (!("geolocation" in navigator) || hasInitializedCenterFromGeo) {
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCenter({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
                setHasInitializedCenterFromGeo(true);
            },
            () => {
                // If user blocks or error occurs, just mark as initialized and keep default center
                setHasInitializedCenterFromGeo(true);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    }, [hasInitializedCenterFromGeo]);

    const fetchPins = async () => {
        try {
            setIsLoadingPins(true);
            const res = await axios.get<{ message: string; map: PinLocation[] }>(
                `${BASE_URL}/admin/map`,
                {
                    params: {
                        longitude: center.lng,
                        latitude: center.lat,
                        radius,
                    },
                }
            );
            setPins(res.data.map);
        } catch (error) {
            setPins([]);
        } finally {
            setIsLoadingPins(false);
        }
    };

    const fetchAutocomplete = async (query: string) => {
        if (!query.trim()) {
            setSuggestions([]);
            return;
        }

        try {
            setIsLoadingSuggestions(true);
            const url = "https://api.olamaps.io/places/v1/autocomplete";
            const response = await fetch(
                `${url}?input=${encodeURIComponent(
                    query
                )}&strictbounds=true&language=en&types=&api_key=${olaMapsApiKey}`,
                {
                    headers: {
                        "X-Request-Id": "",
                        "X-Correlation-Id": "",
                    },
                }
            );

            const data = await response.json();

            if (response.status === 200 && data.status === "ok") {
                setSuggestions(data.predictions || []);
            } else if (response.status === 400 && data.status === "FAILURE") {
                // Invalid input etc.
                setSuggestions([]);
            } else {
                setSuggestions([]);
            }
        } catch {
            setSuggestions([]);
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current || !hasInitializedCenterFromGeo)
            return;

        const initMap = async () => {
            const olaMaps = new OlaMaps({
                apiKey: olaMapsApiKey,
            });

            const zoom = calculateZoom(radius);

            const myMap = await olaMaps.init({
                style:
                    "https://api.olamaps.io/tiles/vector/v1/styles/default-dark-standard/style.json",
                container: mapContainerRef.current,
                center: [center.lng, center.lat],
                zoom,
            });

            myMap.on("load", () => {
                if (myMap.getLayer("3d_model_data")) {
                    myMap.removeLayer("3d_model_data");
                }
            });

            mapRef.current = myMap;
            setIsMapLoaded(true);
        };

        initMap().catch(() => {
            setIsMapLoaded(false);
        });
    }, [center.lat, center.lng, radius, hasInitializedCenterFromGeo]);

    // Keep zoom level in sync when radius changes after map is initialized
    useEffect(() => {
        if (!mapRef.current) return;
        const zoom = calculateZoom(radius);
    mapRef.current.setCenter([center.lng, center.lat]);
    mapRef.current.setZoom(zoom);
  }, [radius, center.lng, center.lat]);

    useEffect(() => {
        fetchPins();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center.lat, center.lng, radius]);

    useEffect(() => {
        // IMPORTANT: pins can load before map initializes.
        // Re-run marker building once the map is loaded.
        if (!mapRef.current || !isMapLoaded) return;

        markersRef.current.forEach((marker) => {
            try {
                marker.remove();
            } catch {
                // ignore
            }
        });
        markersRef.current = [];

        const buildMarkers = async () => {
            for (const pin of pins) {
                const coords = pin?.location?.coordinates;
                if (!Array.isArray(coords) || coords.length < 2) {
                    continue;
                }
                const [lng, lat] = coords;

                // Fetch fresh details for this user based on role
                let details: PopulatedUser | undefined = pin.userId;
                try {
                    if (pin.userId) {
                        const url =
                            pin.role === "teacher"
                                ? `${BASE_URL}/admin/map/teacher/${pin.userId}`
                                : `${BASE_URL}/admin/map/parent/${pin.userId}`;

                        const res = await axios.get(url);
                        details = (res.data.teacher || res.data.parent || details) as PopulatedUser;
                    }
                } catch {
                    // fallback to pin.userId if request fails
                }

                const popupHtml = `
        <div style="position: relative; min-width: 200px; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; color: #fff; padding-right: 18px; color: #000;">
          <button
            type="button"
            class="lm-popup-close"
            style="position:absolute; top:2px; right:2px; background:none; border:none; color:#9ca3af; cursor:pointer; font-size:12px; line-height:1;"
          >
            ×
          </button>
          <div style="font-weight: 600; margin-bottom: 4px;">${details?.name || "Unknown"}</div>
          <div style="margin-bottom: 2px;">${pin.role}</div>
          <div style="margin-bottom: 2px;">Phone: ${details?.phoneCode && details?.phoneNumber
                        ? `${details.phoneCode} ${details.phoneNumber}`
                        : "N/A"
                    }</div>
          <div>Email: ${details?.email || "N/A"}</div>
        </div>
      `;

                const popup = new (OlaMaps as any).Popup({
                    offset: [0, -10],
                    anchor: "bottom",
                }).setHTML(popupHtml);


                const markerElement = document.createElement("div");
                markerElement.style.height = "25px";
                markerElement.style.width = "25px";
                markerElement.style.borderRadius = "50%";
                markerElement.style.backgroundImage = `url(${pin.role === "teacher" ? details?.profilePicture ? details.profilePicture === 'undefined' ? '/avatar.svg' : details.profilePicture || "/avatar.svg"
                    : '/avatar.svg' : '/parent-map.svg'})`;
                markerElement.style.backgroundSize = "cover";
                markerElement.style.backgroundPosition = "center";
                markerElement.style.backgroundRepeat = "no-repeat";
                markerElement.style.border =
                    pin.role === "teacher" ? "2px solid #fcd307" : "2px solid #38bdf8";
                markerElement.style.backgroundColor =
                    pin.role === "teacher" ? "#1e293b" : "#020617";
                markerElement.style.boxShadow =
                    pin.role === "teacher"
                        ? "0 0 10px rgba(252, 211, 7, 0.6)"
                        : "0 0 10px rgba(56, 189, 248, 0.6)";

                const marker = new (OlaMaps as any).Marker({
                    element: markerElement,
                    draggable: false,
                })
                    .setLngLat([lng, lat])
                    .setPopup(popup)
                    .addTo(mapRef.current);

                // Wire close button inside popup
                markerElement.addEventListener("click", () => {
                    // Wait a tick for popup DOM to mount
                    setTimeout(() => {
                        const popupEl = document.querySelector(".ol-popup") as HTMLElement | null;
                        const closeBtn =
                            popupEl?.querySelector<HTMLButtonElement>(".lm-popup-close");
                        if (closeBtn) {
                            closeBtn.onclick = () => {
                                popup.remove();
                            };
                        }
                    }, 0);
                });

                markersRef.current.push(marker);
            }
        };

        void buildMarkers();
    }, [pins, isMapLoaded]);

    const handleSuggestionSelect = (prediction: AutocompletePrediction) => {
        if (prediction.geometry?.location) {
            const { lat, lng } = prediction.geometry.location;
            setCenter({ lat, lng });
            const zoom = calculateZoom(radius);
            if (mapRef.current) {
                mapRef.current.setCenter([lng, lat]);
                mapRef.current.setZoom(zoom);
            }
        }
        setSearchQuery(prediction.description);
        setSuggestions([]);
    };

    const handleSearchChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        setSearchQuery(value);

        if (autocompleteTimerRef.current) {
            window.clearTimeout(autocompleteTimerRef.current);
        }

        autocompleteTimerRef.current = window.setTimeout(() => {
            fetchAutocomplete(value);
        }, 400);
    };

    const radiusInKm = (radius / 1000).toFixed(0);

    return (
        <div className="flex flex-col gap-4 h-full w-full">
      <div className="flex flex-col gap-2">
        <div className="relative max-w-md">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search place (Ola Maps autocomplete)"
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setSuggestions([]);
              }}
              className="shrink-0 rounded-md border border-amber-400 bg-amber-400 px-2 py-1 text-xs font-medium text-slate-900 hover:bg-amber-300 hover:border-amber-300"
            >
              Clear
            </button>
            {isLoadingSuggestions && (
              <div className="absolute right-3 top-2.5 h-4 w-4 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
            )}
          </div>

          {suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-auto rounded-md border border-slate-700 bg-slate-300 text-sm shadow-lg">
              {suggestions.map((prediction, index) => (
                <button
                  key={index}
                   onClick={() => handleSuggestionSelect(prediction)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-black bg-white hover:bg-yellow-400"
                >
                  <span className="truncate">{prediction.description}</span>
                  <span className="ml-2 text-xs text-slate-500">➜</span>
                </button>
              ))}
            </div>
          )}
        </div>

                <div className="flex items-center gap-3 text-xs text-slate-300">
                    <span className="uppercase tracking-wide text-slate-400">
                        Radius
                    </span>
                    <input
                        type="range"
                        min={1000}
                        max={500 * 1000}
                        step={5000}
                        value={radius}
                        onChange={(e) => setRadius(Number(e.target.value))}
                        className="flex-1"
                    />
                    <span className="rounded-sm bg-slate-900 px-2 py-1 text-amber-300">
                        {radiusInKm} km
                    </span>
                    <button
                        type="button"
                        onClick={() => setRadius(5000)}
                        className="rounded-md border border-amber-400 bg-amber-400 px-2 py-1 text-[10px] font-medium text-slate-900 hover:bg-amber-300 hover:border-amber-300"
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="relative h-[500px] w-full rounded-md bg-slate-900 overflow-hidden">
                <div
                    ref={mapContainerRef}
                    className="h-full w-full"
                >
                    {!isMapLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                            Loading map...
                        </div>
                    )}
                </div>

                {isLoadingPins && (
                    <div className="pointer-events-none absolute left-3 top-3 rounded-md bg-slate-900/80 px-2 py-1 text-xs text-slate-200">
                        Loading pins...
                    </div>
                )}

                {!isLoadingPins && pins.length === 0 && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-3 mx-auto w-max rounded-md bg-slate-900/80 px-3 py-1 text-xs text-slate-200">
                        No parents or teachers found in this area.
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapView;